import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { apiClient } from "../utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Video,
  Send,
  Pencil,
  Trash2,
  MapPin,
  Clock,
  User,
  ChevronRight,
  AlertTriangle,
  CalendarCheck,
  XCircle,
  CheckCircle,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { format, set } from "date-fns";
import { DateTimePicker } from "@/components/ui/date-time-picker";


export const SHOOT_TYPES = [
  "Product Showcase",
  "Testimonials",
  "Founder Content",
  "Speech Type",
  "Educational Content",
  "Awareness",
];

const statusColors = {
  planned: "bg-sky-100 text-sky-700 border-sky-200",
  completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
  sent_for_editing: "bg-violet-100 text-violet-700 border-violet-200",
};

const statusDot = {
  planned: "bg-sky-400",
  completed: "bg-emerald-400",
  sent_for_editing: "bg-violet-400",
};

const emptyForm = {
  client_name: "",
  client_id: "",
  date_time: "",
  location: "",
  shoot_type: "",
  assigned_team: [],
};

export default function ShootsPage() {
  const { user } = useAuth();
  const [shoots, setShoots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarConnected, setCalendarConnected] = useState(null);

  // Create dialog
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [statusLoading, setStatusLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editingShoot, setEditingShoot] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingShoot, setDeletingShoot] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Status change dialog
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusShoot, setStatusShoot] = useState(null);
  const [newStatus, setNewStatus] = useState("");

  // Editor selection dialog
  const [editorDialogOpen, setEditorDialogOpen] = useState(false);
  const [selectedShoot, setSelectedShoot] = useState(null);
  const [selectedEditorId, setSelectedEditorId] = useState("");
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingEditors, setLoadingEditors] = useState(false);

  useEffect(() => {
    fetchShoots();
    fetchTeamMembers();
    fetchCalendarStatus();
  }, []);

  const fetchCalendarStatus = async () => {
    try {
      const { data } = await apiClient.get("/calendar/status");
      setCalendarConnected(data.connected);
    } catch {
      setCalendarConnected(false);
    }
  };

  const connectCalendar = async () => {
    try {
      const { data } = await apiClient.get("/calendar/connect");
      window.open(data.authorization_url, "_blank");
      toast.info("Complete Google sign-in, then refresh this page");
    } catch {
      toast.error("Failed to initiate Google Calendar connection");
    }
  };

  const fetchShoots = async () => {
    try {
      const response = await apiClient.get("/shoots");
      setShoots(response.data);
    } catch (error) {
      toast.error("Failed to load shoots");
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    setLoadingEditors(true);
    try {
      const response = await apiClient.get("/users");
      setTeamMembers(response.data.filter((u) => u.role !== "owner"));
    } catch {
    } finally {
      setLoadingEditors(false);
    }
  };

  // ── CREATE ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        date_time: new Date(formData.date_time).toISOString(),
      };
      await apiClient.post("/shoots", payload);
      toast.success("Shoot scheduled successfully");
      setOpen(false);
      setFormData(emptyForm);
      fetchShoots();
    } catch {
      toast.error("Failed to schedule shoot");
    }
  };

  // ── EDIT ──
  const openEdit = (shoot) => {
    setEditingShoot(shoot);
    const dt = new Date(shoot.date_time);
    const localDT = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    setEditForm({
      client_name: shoot.client_name,
      client_id: shoot.client_id || "",
      date_time: localDT,
      location: shoot.location,
      shoot_type: shoot.shoot_type,
      assigned_team: shoot.assigned_team || [],
    });
    setEditOpen(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      const payload = {
        client_name: editForm.client_name,
        location: editForm.location,
        shoot_type: editForm.shoot_type,
        date_time: new Date(editForm.date_time).toISOString(),
      };
      await apiClient.patch(`/shoots/${editingShoot.id}`, payload);
      toast.success("Shoot updated successfully");
      setEditOpen(false);
      setEditingShoot(null);
      fetchShoots();
    } catch {
      toast.error("Failed to update shoot");
    } finally {
      setEditLoading(false);
    }
  };

  // ── DELETE ──
  const openDelete = (shoot) => {
    setDeletingShoot(shoot);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await apiClient.delete(`/shoots/${deletingShoot.id}`);
      toast.success("Shoot deleted");
      setDeleteOpen(false);
      setDeletingShoot(null);
      fetchShoots();
    } catch {
      toast.error("Failed to delete shoot");
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── STATUS CHANGE ──
  const openStatusChange = (shoot) => {
    if (shoot.status === "completed") {
      openSendForEditingDialog(shoot);
      return;
    }
    setStatusShoot(shoot);
    const next =
      shoot.status === "planned"
        ? "completed"
        : shoot.status === "completed"
          ? "sent_for_editing"
          : "planned";
    setNewStatus(next);
    setStatusOpen(true);
  };

  const confirmStatusChange = async () => {
    setStatusLoading(true);
    try {
      await apiClient.patch(`/shoots/${statusShoot.id}`, { status: newStatus });
      toast.success("Status updated");
      setStatusOpen(false);
      fetchShoots();
    } catch {
      toast.error("Failed to update status");
    } finally {
      setStatusLoading(false);
    }
  };

  // ── SEND FOR EDITING ──
  const openSendForEditingDialog = (shoot) => {
    setSelectedShoot(shoot);
    setSelectedEditorId("");
    setEditorDialogOpen(true);
  };

  const confirmSendForEditing = async () => {
    if (!selectedEditorId) {
      toast.error("Please select a video editor");
      return;
    }
    try {
      await apiClient.patch(`/shoots/${selectedShoot.id}`, {
        status: "sent_for_editing",
        assigned_editor: selectedEditorId,
      });
      toast.success("Shoot sent for editing");
      setEditorDialogOpen(false);
      setSelectedShoot(null);
      setSelectedEditorId("");
      fetchShoots();
    } catch {
      toast.error("Failed to send shoot for editing");
    }
  };

  const isManager = user?.role === "owner" || user?.role === "pillar";

  return (
    <div className="space-y-6" data-testid="shoots-page">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1
            className="text-4xl font-bold"
            style={{ letterSpacing: "-0.02em" }}
          >
            Shoots
          </h1>
          <p className="text-muted-foreground mt-1">
            Plan and manage photo/video shoots
          </p>
        </div>
        {isManager && (
          <div className="flex gap-2 shrink-0">
            {" "}
            {calendarConnected === false && (
              <Button variant="outline" size="sm" onClick={connectCalendar}>
                <CalendarCheck className="w-4 h-4 mr-2" />
                Connect Calendar
              </Button>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="plan-shoot-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Plan Shoot
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Plan New Shoot</DialogTitle>
                  <DialogDescription>Enter shoot details</DialogDescription>
                </DialogHeader>
                <ShootForm
                  formData={formData}
                  setFormData={setFormData}
                  onSubmit={handleSubmit}
                  submitLabel="Schedule Shoot"
                />
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>
      {calendarConnected !== null && (
        <div
          className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-sm ${
            calendarConnected
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-amber-50 border-amber-200 text-amber-800"
          }`}
        >
          {calendarConnected ? (
            <CheckCircle className="w-4 h-4 shrink-0" />
          ) : (
            <XCircle className="w-4 h-4 shrink-0" />
          )}
          {calendarConnected
            ? "Google Calendar connected — shoots will auto-create calendar events with a 10-min reminder."
            : "Google Calendar not connected — connect it to auto-create events when shoots are scheduled."}
        </div>
      )}
      {/* ── Edit Dialog ── */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Shoot</DialogTitle>
            <DialogDescription>Update shoot details</DialogDescription>
          </DialogHeader>
          <ShootForm
            formData={editForm}
            setFormData={setEditForm}
            onSubmit={handleEdit}
            loading={editLoading}
            submitLabel="Save Changes"
          />
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirm Dialog ── */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete Shoot
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the shoot for{" "}
              <strong>{deletingShoot?.client_name}</strong>? This cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end mt-2">
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? "Deleting…" : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Status Change Dialog ── */}
      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Change Status</DialogTitle>
            <DialogDescription>
              Update status for <strong>{statusShoot?.client_name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {["planned", "completed", "sent_for_editing"].map((s) => (
              <button
                key={s}
                onClick={() => setNewStatus(s)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-all ${
                  newStatus === s
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border hover:border-muted-foreground/40"
                }`}
              >
                <span className={`w-2.5 h-2.5 rounded-full ${statusDot[s]}`} />
                <span className="text-sm font-medium capitalize">
                  {s.replace(/_/g, " ")}
                </span>
                {newStatus === s && (
                  <ChevronRight className="w-4 h-4 ml-auto text-primary" />
                )}
              </button>
            ))}
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setStatusOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmStatusChange} disabled={statusLoading}>
              {statusLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying…
                </>
              ) : (
                "Apply Status"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Editor Selection Dialog ── */}
      <Dialog open={editorDialogOpen} onOpenChange={setEditorDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Send for Editing
            </DialogTitle>
            <DialogDescription>
              {selectedShoot && (
                <>
                  Assign a video editor for{" "}
                  <strong>{selectedShoot.client_name}</strong> —{" "}
                  {selectedShoot.shoot_type}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="editor_select">Select Video Editor *</Label>
              {loadingEditors ? (
                <p className="text-sm text-muted-foreground mt-1">Loading…</p>
              ) : teamMembers.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-1">
                  No team members found.
                </p>
              ) : (
                <select
                  id="editor_select"
                  className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                  value={selectedEditorId}
                  onChange={(e) => setSelectedEditorId(e.target.value)}
                >
                  <option value="">— Choose an editor —</option>
                  {teamMembers.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} ({m.role})
                    </option>
                  ))}
                </select>
              )}
            </div>
            {selectedEditorId && (
              <div className="rounded-md bg-muted px-4 py-3 text-sm">
                <span className="font-medium">
                  {teamMembers.find((m) => m.id === selectedEditorId)?.name}
                </span>{" "}
                will receive a notification and editing task for this shoot.
              </div>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => setEditorDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmSendForEditing}
              disabled={!selectedEditorId}
            >
              <Send className="w-4 h-4 mr-2" />
              Confirm & Send
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Shoot Cards ── */}
      {loading ? (
        <p className="text-muted-foreground">Loading shoots...</p>
      ) : shoots.length === 0 ? (
        <Card className="p-8 text-center">
          <Video className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No shoots scheduled yet.</p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {shoots.map((shoot) => (
            <Card
              key={shoot.id}
              className="p-0 overflow-hidden hover-lift border border-border"
            >
              {/* colour stripe */}
              <div
                className={`h-1 w-full ${
                  shoot.status === "planned"
                    ? "bg-sky-400"
                    : shoot.status === "completed"
                      ? "bg-emerald-400"
                      : "bg-violet-400"
                }`}
              />
              <div className="p-5">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <h3 className="text-lg font-semibold truncate">
                        {shoot.client_name}
                      </h3>
                      <Badge
                        className={`text-xs ${statusColors[shoot.status]}`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full mr-1.5 ${statusDot[shoot.status]}`}
                        />
                        {shoot.status.replace(/_/g, " ")}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Video className="w-3.5 h-3.5 shrink-0" />
                        <span className="font-medium text-foreground">
                          {shoot.shoot_type}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <MapPin className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{shoot.location}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Clock className="w-3.5 h-3.5 shrink-0" />
                        <span>{format(new Date(shoot.date_time), "PPp")}</span>
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end max-w-[200px] sm:max-w-none">
                    {/* Status actions */}
                    {shoot.status === "planned" && isManager && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(shoot.id, "completed")}
                        className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                      >
                        Mark Completed
                      </Button>
                    )}
                    {shoot.status === "completed" && isManager && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openSendForEditingDialog(shoot)}
                        className="text-violet-600 border-violet-200 hover:bg-violet-50"
                      >
                        <Send className="w-3 h-3 mr-1" />
                        Send for Editing
                      </Button>
                    )}

                    {/* Change status button (all statuses) */}
                    {isManager && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (shoot.status === "completed") {
                            openSendForEditingDialog(shoot);
                          } else {
                            openStatusChange(shoot);
                          }
                        }}
                        className="text-muted-foreground"
                        title="Change Status"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    )}

                    {/* Edit */}
                    {isManager && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(shoot)}
                        className="text-muted-foreground hover:text-foreground"
                        title="Edit Shoot"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}

                    {/* Delete */}
                    {user?.role === "owner" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openDelete(shoot)}
                        className="text-muted-foreground hover:text-destructive"
                        title="Delete Shoot"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  function updateStatus(shootId, status) {
    apiClient
      .patch(`/shoots/${shootId}`, { status })
      .then(() => {
        toast.success("Shoot status updated");
        fetchShoots();
      })
      .catch(() => toast.error("Failed to update shoot"));
  }
}

function ShootForm({ formData, setFormData, onSubmit, submitLabel, loading }) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <Label htmlFor="client_name">Client Name *</Label>
        <Input
          id="client_name"
          value={formData.client_name}
          onChange={(e) =>
            setFormData({ ...formData, client_name: e.target.value })
          }
          required
        />
      </div>
      <div>
        <Label htmlFor="shoot_type">Shoot Type *</Label>
        <select
          id="shoot_type"
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
          value={formData.shoot_type}
          onChange={(e) =>
            setFormData({ ...formData, shoot_type: e.target.value })
          }
          required
        >
          <option value="">Select shoot type</option>
          {SHOOT_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>
      <div>
        <Label htmlFor="location">Location *</Label>
        <Input
          id="location"
          value={formData.location}
          onChange={(e) =>
            setFormData({ ...formData, location: e.target.value })
          }
          required
        />
      </div>
      <div>
        <Label htmlFor="date_time">Date & Time *</Label>
        <div className="mt-1">
          <DateTimePicker
            value={formData.date_time}
            onChange={(val) => setFormData({ ...formData, date_time: val })}
            placeholder="Select shoot date and time"
          />
        </div>
      </div>
      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? "Saving..." : submitLabel}
      </Button>
    </form>
  );
}
